import React, { useCallback, useMemo, useState } from "react";
import Breadcrums from "../components/breadcrums";
import PageTitle from "../components/pageTitle";
import { employeesImportBreadcrums } from "../constants/breadcrums";
import { employeesCommandActions, button, buttonSM, buttonBase, blueBgClr, lightBgClr, redBgClr, sectionClass } from "../constants/common";
import { addUpdateConsultant, listOfEmployees } from "../api/consultants-api";
import { useSnackbar } from "react-simple-snackbar";
import Papa from "papaparse";
import moment from "moment";
import logger from "../utils/logger";
import { Md5 } from "ts-md5";
import classNames from "classnames";

type Step = "upload" | "preview" | "importing" | "done";

interface ParsedRow {
    _rowNumber: number;
    name: string;
    email: string;
    phone: string;
    designation: string;
    dob: string;
    masked_ssn: string;
    date_of_joining: string;
    entity: string;
    _errors: string[];
    _valid: boolean;
}

interface ImportResult {
    rowNumber: number;
    name: string;
    email: string;
    success: boolean;
    error?: string;
}

const TEMPLATE_HEADERS = ["name", "email", "phone", "designation", "dob", "masked_ssn", "date_of_joining", "entity"];

const TEMPLATE_SAMPLE_ROWS = [
    ["John Smith", "john.smith@example.com", "555-0101", "Software Engineer", "1990-05-15", "XXX-XX-1234", "2024-01-15", "MTINC"],
    ["Jane Doe", "jane.doe@example.com", "555-0102", "Project Manager", "1985-08-22", "XXX-XX-5678", "2023-06-01", "MTINC"],
];

const VALID_ENTITIES = ["MTINC", "MTIND"];

function validateRow(row: Record<string, string>, rowIndex: number, existingEmails: Set<string>, emailSet: Set<string>): ParsedRow {
    const errors: string[] = [];
    const name = (row.name || "").trim();
    const email = (row.email || "").trim().toLowerCase();
    const phone = (row.phone || "").trim();
    const designation = (row.designation || "").trim();
    const dob = (row.dob || "").trim();
    const masked_ssn = (row.masked_ssn || "").trim();
    const date_of_joining = (row.date_of_joining || "").trim();
    const entity = (row.entity || "MTINC").trim().toUpperCase();

    if (!name) {
        errors.push("Name is required");
    }

    if (!email) {
        errors.push("Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push("Invalid email format");
    } else if (existingEmails.has(email)) {
        errors.push("Email already exists in system");
    } else if (emailSet.has(email)) {
        errors.push("Duplicate email in CSV");
    }

    if (dob && !moment(dob, ["YYYY-MM-DD", "MM/DD/YYYY", "MM-DD-YYYY"], true).isValid()) {
        errors.push("Invalid date of birth format (use YYYY-MM-DD or MM/DD/YYYY)");
    }

    if (date_of_joining && !moment(date_of_joining, ["YYYY-MM-DD", "MM/DD/YYYY", "MM-DD-YYYY"], true).isValid()) {
        errors.push("Invalid date of joining format (use YYYY-MM-DD or MM/DD/YYYY)");
    }

    if (entity && !VALID_ENTITIES.includes(entity)) {
        errors.push(`Invalid entity "${entity}". Must be ${VALID_ENTITIES.join(" or ")}`);
    }

    return {
        _rowNumber: rowIndex,
        name,
        email,
        phone,
        designation,
        dob,
        masked_ssn,
        date_of_joining,
        entity: entity || "MTINC",
        _errors: errors,
        _valid: errors.length === 0,
    };
}

function formatDateForDb(dateStr: string): string | null {
    if (!dateStr) return null;
    const m = moment(dateStr, ["YYYY-MM-DD", "MM/DD/YYYY", "MM-DD-YYYY"], true);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

function downloadFile(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default function ImportEmployees() {
    const [openSnackbar] = useSnackbar();
    const [step, setStep] = useState<Step>("upload");
    const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
    const [importResults, setImportResults] = useState<ImportResult[]>([]);
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
    const [isProcessing, setIsProcessing] = useState(false);

    const validRows = useMemo(() => parsedRows.filter((r) => r._valid), [parsedRows]);
    const invalidRows = useMemo(() => parsedRows.filter((r) => !r._valid), [parsedRows]);

    const handleTemplateDownload = useCallback(() => {
        const csv = Papa.unparse({
            fields: TEMPLATE_HEADERS,
            data: TEMPLATE_SAMPLE_ROWS,
        });
        downloadFile(csv, "employee_import_template.csv", "text/csv;charset=utf-8;");
    }, []);

    const handleFileUpload = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (!file.name.endsWith(".csv")) {
                openSnackbar("Please select a CSV file");
                return;
            }

            setIsProcessing(true);

            try {
                const existingEmployees = await listOfEmployees();
                const existingEmails = new Set<string>(
                    (existingEmployees || []).map((emp: any) => (emp.email || "").trim().toLowerCase())
                );

                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        const emailSet = new Set<string>();
                        const rows: ParsedRow[] = (results.data as Record<string, string>[]).map((row, idx) => {
                            const parsed = validateRow(row, idx + 2, existingEmails, emailSet);
                            if (parsed.email) {
                                emailSet.add(parsed.email);
                            }
                            return parsed;
                        });

                        setParsedRows(rows);
                        setStep("preview");
                        setIsProcessing(false);
                    },
                    error: (err) => {
                        logger.error("CSV parse error", { error: err });
                        openSnackbar("Error reading CSV file: " + err.message);
                        setIsProcessing(false);
                    },
                });
            } catch (err) {
                logger.error("Failed to fetch existing employees for validation", {}, err);
                openSnackbar("Failed to validate against existing employees. Please try again.");
                setIsProcessing(false);
            }

            e.target.value = "";
        },
        [openSnackbar]
    );

    const handleImport = useCallback(async () => {
        if (validRows.length === 0) return;

        setStep("importing");
        setIsProcessing(true);
        setImportProgress({ current: 0, total: validRows.length });

        const results: ImportResult[] = [];

        for (let i = 0; i < validRows.length; i++) {
            const row = validRows[i];
            setImportProgress({ current: i + 1, total: validRows.length });

            try {
                const consultantData: any = {
                    uuid: Md5.hashStr(row.email),
                    active: 1,
                    name: row.name,
                    email: row.email,
                    phone: row.phone || null,
                    designation: row.designation || null,
                    dob: formatDateForDb(row.dob),
                    masked_ssn: row.masked_ssn || null,
                    date_of_joining: formatDateForDb(row.date_of_joining),
                    date_of_exit: null,
                    entity: row.entity || "MTINC",
                    status: null,
                    bgv: null,
                    bgv_by: null,
                    bgv_dt: null,
                    street: null,
                    city: null,
                    district: null,
                    state: null,
                    zip: null,
                    country: null,
                };

                const result = await addUpdateConsultant(consultantData);
                const success = result && !result.error;
                results.push({
                    rowNumber: row._rowNumber,
                    name: row.name,
                    email: row.email,
                    success,
                    error: result?.error?.sqlMessage || result?.error || undefined,
                });
            } catch (err: any) {
                results.push({
                    rowNumber: row._rowNumber,
                    name: row.name,
                    email: row.email,
                    success: false,
                    error: err.message || "Unknown error",
                });
            }
        }

        setImportResults(results);
        setIsProcessing(false);
        setStep("done");

        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;

        if (failCount === 0) {
            openSnackbar(`Successfully imported ${successCount} employee(s).`);
        } else {
            openSnackbar(`Imported ${successCount} employee(s). ${failCount} failed. Download the error report for details.`);
        }
    }, [validRows, openSnackbar]);

    const handleDownloadErrorReport = useCallback(() => {
        const failedRows = importResults.filter((r) => !r.success);
        if (failedRows.length === 0) return;

        const csv = Papa.unparse({
            fields: ["row_number", "name", "email", "error"],
            data: failedRows.map((r) => [r.rowNumber, r.name, r.email, r.error || "Unknown error"]),
        });
        const timestamp = moment().format("YYYYMMDD_HHmmss");
        downloadFile(csv, `employee_import_errors_${timestamp}.csv`, "text/csv;charset=utf-8;");
    }, [importResults]);

    const handleDownloadValidationErrors = useCallback(() => {
        if (invalidRows.length === 0) return;

        const csv = Papa.unparse({
            fields: ["row_number", "name", "email", "error_messages"],
            data: invalidRows.map((r) => [r._rowNumber, r.name, r.email, r._errors.join("; ")]),
        });
        const timestamp = moment().format("YYYYMMDD_HHmmss");
        downloadFile(csv, `employee_import_validation_errors_${timestamp}.csv`, "text/csv;charset=utf-8;");
    }, [invalidRows]);

    const handleReset = useCallback(() => {
        setStep("upload");
        setParsedRows([]);
        setImportResults([]);
        setImportProgress({ current: 0, total: 0 });
    }, []);

    const successCount = importResults.filter((r) => r.success).length;
    const failCount = importResults.filter((r) => !r.success).length;

    return (
        <main>
            <Breadcrums breadcrumLinks={employeesImportBreadcrums} module={"Employees"} groups={[]} commands={employeesCommandActions} />
            <PageTitle title={"Import Employees"} preSummary={"Bulk Import"} summary={"Import employees from a CSV file."} />

            {/* Step indicator */}
            <div className={sectionClass}>
                <nav aria-label="Progress" className="mb-6">
                    <ol className="flex items-center">
                        {[
                            { key: "upload", label: "Upload & Validate" },
                            { key: "preview", label: "Preview" },
                            { key: "done", label: "Results" },
                        ].map((s, idx) => {
                            const isActive = step === s.key || (step === "importing" && s.key === "preview");
                            const isComplete = step === "done" && s.key !== "done";
                            return (
                                <li key={s.key} className={classNames("flex items-center", idx < 2 ? "mr-4" : "")}>
                                    <span
                                        className={classNames(
                                            "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                                            isComplete ? "bg-green-500 text-white" : isActive ? "bg-blue-700 text-white" : "bg-gray-200 text-gray-500"
                                        )}
                                    >
                                        {isComplete ? "✓" : idx + 1}
                                    </span>
                                    <span className={classNames("ml-2 text-sm font-medium", isActive ? "text-gray-900" : "text-gray-500")}>{s.label}</span>
                                    {idx < 2 && <div className="flex-shrink-0 w-10 h-px bg-gray-300 ml-4" />}
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            </div>

            {/* Step 1: Upload */}
            {step === "upload" && (
                <section className={sectionClass}>
                    <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                        <div className="md:grid md:grid-cols-3 md:gap-6">
                            <div className="md:col-span-1">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Upload CSV File</h3>
                                <p className="mt-1 text-xs text-gray-500">
                                    Select a CSV file with employee data. The file must include a header row with columns: {TEMPLATE_HEADERS.join(", ")}.
                                </p>
                                <div className="mt-4">
                                    <button onClick={handleTemplateDownload} type="button" className={classNames(button, buttonSM, blueBgClr)}>
                                        Download Template
                                    </button>
                                </div>
                            </div>
                            <div className="mt-5 md:mt-0 md:col-span-2">
                                <div className="flex items-center space-x-4">
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileUpload}
                                        disabled={isProcessing}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                                {isProcessing && (
                                    <div className="mt-4 flex items-center text-sm text-gray-500">
                                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Validating rows...
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Step 2: Preview */}
            {step === "preview" && (
                <section className={sectionClass}>
                    <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                        <div className="md:grid md:grid-cols-3 md:gap-6">
                            <div className="md:col-span-1">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Preview Import</h3>
                                <p className="mt-1 text-xs text-gray-500">Review the parsed data below. Rows with errors will be skipped during import.</p>

                                <div className="mt-4 space-y-2">
                                    <div className="text-sm">
                                        <span className="font-medium text-gray-900">{parsedRows.length}</span> <span className="text-gray-500">total rows</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-medium text-green-700">{validRows.length}</span> <span className="text-gray-500">valid rows</span>
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-medium text-red-700">{invalidRows.length}</span> <span className="text-gray-500">rows with errors</span>
                                    </div>
                                </div>

                                <div className="mt-4 space-y-2">
                                    <button onClick={handleImport} disabled={validRows.length === 0 || isProcessing} type="button" className={classNames(button, buttonBase, blueBgClr, validRows.length === 0 && "opacity-50 cursor-not-allowed")}>
                                        Import {validRows.length} Record{validRows.length !== 1 ? "s" : ""}
                                    </button>
                                    {invalidRows.length > 0 && (
                                        <button onClick={handleDownloadValidationErrors} type="button" className={classNames(button, buttonBase, redBgClr)}>
                                            Download Error Report
                                        </button>
                                    )}
                                    <button onClick={handleReset} type="button" className={classNames(button, buttonBase, lightBgClr)}>
                                        Back
                                    </button>
                                </div>
                            </div>
                            <div className="mt-5 md:mt-0 md:col-span-2">
                                <div className="overflow-auto max-h-[500px] border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">#</th>
                                                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Status</th>
                                                {TEMPLATE_HEADERS.map((h) => (
                                                    <th key={h} className="px-2 py-2 text-left font-medium text-gray-500 uppercase">
                                                        {h}
                                                    </th>
                                                ))}
                                                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Errors</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {parsedRows.map((row) => (
                                                <tr key={row._rowNumber} className={classNames(row._valid ? "" : "bg-red-50")}>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-500">{row._rowNumber}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">
                                                        {row._valid ? (
                                                            <span className="text-green-600 font-medium">✓</span>
                                                        ) : (
                                                            <span className="text-red-600 font-medium">✗</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.name}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.email}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.phone}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.designation}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.dob}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.masked_ssn}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.date_of_joining}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-gray-900">{row.entity}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap text-red-600 max-w-[200px] truncate" title={row._errors.join("; ")}>
                                                        {row._errors.join("; ")}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Step: Importing (progress) */}
            {step === "importing" && (
                <section className={sectionClass}>
                    <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                        <div className="md:grid md:grid-cols-3 md:gap-6">
                            <div className="md:col-span-1">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Importing...</h3>
                                <p className="mt-1 text-xs text-gray-500">Processing employee records. Please do not close this page.</p>
                            </div>
                            <div className="mt-5 md:mt-0 md:col-span-2">
                                <div className="w-full bg-gray-200 rounded-full h-4">
                                    <div
                                        className="bg-blue-700 h-4 rounded-full transition-all duration-300"
                                        style={{ width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
                                    />
                                </div>
                                <p className="mt-2 text-sm text-gray-600">
                                    Processing {importProgress.current} of {importProgress.total} records...
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Step: Done */}
            {step === "done" && (
                <section className={sectionClass}>
                    <div className="px-4 py-5 bg-white shadow sm:rounded-lg sm:p-6">
                        <div className="md:grid md:grid-cols-3 md:gap-6">
                            <div className="md:col-span-1">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Import Complete</h3>
                                <p className="mt-1 text-xs text-gray-500">Summary of the import operation.</p>

                                <div className="mt-4 space-y-2">
                                    <div className="text-sm">
                                        <span className="font-medium text-green-700">{successCount}</span> <span className="text-gray-500">successfully imported</span>
                                    </div>
                                    {failCount > 0 && (
                                        <div className="text-sm">
                                            <span className="font-medium text-red-700">{failCount}</span> <span className="text-gray-500">failed</span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 space-y-2">
                                    {failCount > 0 && (
                                        <button onClick={handleDownloadErrorReport} type="button" className={classNames(button, buttonBase, redBgClr)}>
                                            Download Error Report
                                        </button>
                                    )}
                                    <button onClick={handleReset} type="button" className={classNames(button, buttonBase, blueBgClr)}>
                                        Import More
                                    </button>
                                    <a href="/employees" className={classNames(button, buttonBase, lightBgClr, "inline-block")}>
                                        Back to Employees
                                    </a>
                                </div>
                            </div>
                            <div className="mt-5 md:mt-0 md:col-span-2">
                                <div className="overflow-auto max-h-[500px] border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Row</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Email</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {importResults.map((r) => (
                                                <tr key={r.rowNumber} className={classNames(r.success ? "" : "bg-red-50")}>
                                                    <td className="px-3 py-1 whitespace-nowrap text-gray-500">{r.rowNumber}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-gray-900">{r.name}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-gray-900">{r.email}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap">
                                                        {r.success ? <span className="text-green-600 font-medium">Success</span> : <span className="text-red-600 font-medium">Failed</span>}
                                                    </td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-red-600 max-w-[200px] truncate" title={r.error}>
                                                        {r.error || ""}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </main>
    );
}
